import { isDefined, toDate } from "@flaminem/utils";
import { useCallback } from "react";

import { useLocale } from "src/internationalization/locale";

import { useT } from ".";

export const useFormatDate = () => {
  const { locale } = useLocale();

  const formatDate = useCallback(
    (date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions) => {
      if (!date) return "";

      try {
        const dateObj = toDate(date);
        if (isNaN(dateObj.getTime())) return "";
        return new Intl.DateTimeFormat(locale, options).format(dateObj);
      } catch (error) {
        console.error("formatDate error", error);
        return "";
      }
    },
    [locale]
  );

  return formatDate;
};

export const useFormatCurrency = () => {
  const { locale } = useLocale();

  const formatCurrency = useCallback(
    (amount: number, options?: Intl.NumberFormatOptions) => {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        ...options,
      }).format(amount);
    },
    [locale]
  );

  return formatCurrency;
};

export const useFormatFloat = () => {
  const { locale } = useLocale();

  const formatFloat = useCallback(
    (number: number | null | undefined, options?: Intl.NumberFormatOptions) => {
      if (!number) return "";
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        ...options,
      }).format(number);
    },
    [locale]
  );

  return formatFloat;
};

export const useFormatInteger = () => {
  const { locale } = useLocale();

  const formatInteger = useCallback(
    (number: number | null | undefined, options?: Intl.NumberFormatOptions) => {
      if (!isDefined(number)) return "";
      return new Intl.NumberFormat(locale, {
        ...options,
        maximumFractionDigits: 0,
      }).format(number);
    },
    [locale]
  );

  return formatInteger;
};

export const useFormatPercentage = () => {
  const { locale } = useLocale();

  const formatPercentage = useCallback(
    (percentage: number | null | undefined, options?: Intl.NumberFormatOptions): string => {
      if (!percentage) return "";
      return Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        ...options,
      }).format(percentage);
    },
    [locale]
  );

  return formatPercentage;
};

export const useFormatBoolean = () => {
  const t = useT();

  const formatBoolean = useCallback(
    (value: boolean) => {
      return value ? t("general.yes") : t("general.no");
    },
    [t]
  );

  return formatBoolean;
};
